// Скрытие костей конечностей на innerMesh, когда все кубы сетки в зоне «отрезаны».
// Зоны (ix,iy,iz) — грубая разметка body-local сетки; при необходимости подкрутить под модель.

import type { LoadedMesh } from "./meshLoader";
import { findJointSubtreeIndices } from "./meshLoader";
import {
  BODY_VOX_SX,
  BODY_VOX_SY,
  BODY_VOX_SZ,
  BODY_VOX_GRID_BYTES,
} from "./bodyVox";
import type { MeshInstance } from "./renderer";

export interface VoxelRegionBox {
  ix0: number;
  ix1: number;
  iy0: number;
  iy1: number;
  iz0: number;
  iz1: number;
}

/** Inclusive ranges в индексах клеток 10×10×24. */
export const LIMB_VOX_REGIONS: Readonly<{
  leftArm: VoxelRegionBox;
  rightArm: VoxelRegionBox;
  leftLeg: VoxelRegionBox;
  rightLeg: VoxelRegionBox;
  /** Верх тела / голова в body-local (серверная сетка 10×10×24). */
  headNeck: VoxelRegionBox;
}> = {
  // Руки: боковые полосы, плечо–кисть по высоте (подстройка под soldier_vox).
  leftArm:  { ix0: 0, ix1: 3, iy0: 2, iy1: 8, iz0: 9,  iz1: 22 },
  rightArm: { ix0: 6, ix1: 9, iy0: 2, iy1: 8, iz0: 9,  iz1: 22 },
  // Ноги: шире по iy/iz (сервер bodyvox.go — те же границы для опоры).
  leftLeg:  { ix0: 0, ix1: 4, iy0: 1, iy1: 9, iz0: 0,  iz1: 12 },
  rightLeg: { ix0: 5, ix1: 9, iy0: 1, iy1: 9, iz0: 0,  iz1: 12 },
  headNeck: { ix0: 3, ix1: 6, iy0: 3, iy1: 6, iz0: 17, iz1: 23 },
};

type DetachBuckets = {
  leftArm: number[];
  rightArm: number[];
  leftLeg: number[];
  rightLeg: number[];
  headNeck: number[];
};

let detachBuckets: DetachBuckets | null = null;

export function initBodyVoxLimbDetach(mesh: LoadedMesh | null): void {
  if (!mesh?.skin) {
    detachBuckets = null;
    return;
  }
  const n = mesh.nodes;
  const sj = mesh.skin.joints;
  // Shoulder включает всё поддерево руки (часто надёжнее, чем только LeftArm).
  detachBuckets = {
    leftArm: findJointSubtreeIndices(n, sj, ["mixamorig:LeftShoulder", "mixamorig:LeftArm"]),
    rightArm: findJointSubtreeIndices(n, sj, ["mixamorig:RightShoulder", "mixamorig:RightArm"]),
    leftLeg: findJointSubtreeIndices(n, sj, ["mixamorig:LeftUpLeg", "mixamorig:LeftLeg"]),
    rightLeg: findJointSubtreeIndices(n, sj, ["mixamorig:RightUpLeg", "mixamorig:RightLeg"]),
    headNeck: findJointSubtreeIndices(n, sj, ["mixamorig:Neck", "mixamorig:Head"]),
  };
}

function cellBitAlive(vox: Uint8Array, cellIdx: number): boolean {
  return ((vox[cellIdx >> 3] >> (cellIdx & 7)) & 1) !== 0;
}

/**
 * Остаток живых клеток в зоне ниже доли — конечность «оторвана» (кости зануляем).
 * Раньше было alive ≤ max(10, 10% total): для большой зоны (рука ~400 клеток)
 * нужно было уничтожить ~90%+ клеток — в игре почти недостижимо.
 */
export function regionConsideredDestroyed(vox: Uint8Array, r: VoxelRegionBox): boolean {
  let alive = 0;
  let total = 0;
  for (let iz = r.iz0; iz <= r.iz1; iz++) {
    for (let iy = r.iy0; iy <= r.iy1; iy++) {
      for (let ix = r.ix0; ix <= r.ix1; ix++) {
        const idx = ix + iy * BODY_VOX_SX + iz * BODY_VOX_SX * BODY_VOX_SY;
        if (idx >= BODY_VOX_SX * BODY_VOX_SY * BODY_VOX_SZ) continue;
        total++;
        if (cellBitAlive(vox, idx)) alive++;
      }
    }
  }
  if (total === 0) return false;
  const maxAliveAllowed = Math.max(6, Math.ceil(total * 0.42));
  return alive <= maxAliveAllowed;
}

function countAliveInRegion(vox: Uint8Array, r: VoxelRegionBox): { alive: number; total: number } {
  let alive = 0;
  let total = 0;
  for (let iz = r.iz0; iz <= r.iz1; iz++) {
    for (let iy = r.iy0; iy <= r.iy1; iy++) {
      for (let ix = r.ix0; ix <= r.ix1; ix++) {
        const idx = ix + iy * BODY_VOX_SX + iz * BODY_VOX_SX * BODY_VOX_SY;
        if (idx >= BODY_VOX_SX * BODY_VOX_SY * BODY_VOX_SZ) continue;
        total++;
        if (cellBitAlive(vox, idx)) alive++;
      }
    }
  }
  return { alive, total };
}

/** Зеркало server BodyVoxLegsCanSupport — для клиентского предикта движения. */
export function bodyVoxLegsCanSupportClient(vox: Uint8Array | null): boolean {
  if (!vox || vox.length !== BODY_VOX_GRID_BYTES) return true;
  const L = countAliveInRegion(vox, LIMB_VOX_REGIONS.leftLeg);
  const R = countAliveInRegion(vox, LIMB_VOX_REGIONS.rightLeg);
  if (L.total === 0 || R.total === 0) return true;
  const minFrac = 0.07;
  return (L.alive / L.total) >= minFrac && (R.alive / R.total) >= minFrac;
}

/** Обновляет список joint-индексов для зануления на innerMesh (отрубленная конечность). */
/** Индексы skin-joints шеи/головы (после initBodyVoxLimbDetach). Для скрытия головы при headshot на outer mesh. */
export function getHeadNeckSkinJointIndices(): number[] {
  return detachBuckets?.headNeck?.length ? [...detachBuckets.headNeck] : [];
}

export function updateInnerMeshLimbDetach(inst: MeshInstance): void {
  if (!detachBuckets || !inst.innerMesh) {
    inst.innerMeshHiddenJoints = null;
    return;
  }
  const vox = inst.bodyVoxAlive;
  if (!vox || vox.length !== BODY_VOX_GRID_BYTES) {
    inst.innerMeshHiddenJoints = null;
    return;
  }
  const out: number[] = [];
  const b = detachBuckets;
  const R = LIMB_VOX_REGIONS;
  if (regionConsideredDestroyed(vox, R.leftArm)) for (const j of b.leftArm) out.push(j);
  if (regionConsideredDestroyed(vox, R.rightArm)) for (const j of b.rightArm) out.push(j);
  if (regionConsideredDestroyed(vox, R.leftLeg)) for (const j of b.leftLeg) out.push(j);
  if (regionConsideredDestroyed(vox, R.rightLeg)) for (const j of b.rightLeg) out.push(j);
  if (regionConsideredDestroyed(vox, R.headNeck)) for (const j of b.headNeck) out.push(j);
  inst.innerMeshHiddenJoints = out.length > 0 ? [...new Set(out)] : null;
}
